import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design02/Header';
import {
    Crown,
    Award,
    Heart,
    Shield,
    Star,
    ArrowLeft,
    CheckCircle,
    Thermometer,
    Droplets,
    Mountain,
    Leaf,
    Sparkles,
    Zap,
    Globe,
    ArrowRight
} from 'lucide-react';

const CinnamonPage = () => {
    return (
        <>
            {/* Head tags (React 19 hoists to <head>) */}
            <title>Ceylon Cinnamon - The True Cinnamon | Claude&apos;s Special</title>
            <meta
                name="description"
                content="Discover authentic Ceylon Cinnamon from Sri Lanka. Learn about the world's finest cinnamon variety, its health benefits, and why it's considered the gold standard of spices."
            />
            <meta
                name="keywords"
                content="Ceylon Cinnamon, true cinnamon, Cinnamomum verum, Sri Lankan cinnamon, health benefits, premium spices"
            />
            <meta property="og:title" content="Ceylon Cinnamon - The True Cinnamon" />
            <meta
                property="og:description"
                content="Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits."
            />
            <meta property="og:type" content="article" />
            <link rel="canonical" href="/ceylon-cinnamon" />
            {/* Optional share image:
        <meta property="og:image" content="https://yourcdn.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ceylon Cinnamon - The True Cinnamon" />
        <meta name="twitter:description" content="Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits." />
        <meta name="twitter:image" content="https://yourcdn.com/og-image.jpg" />
      */}

            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-700"></div>
                    <div className="absolute inset-0 opacity-20">
                        <img
                            src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                            alt="Ceylon Cinnamon bark being harvested"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-amber-400/20 to-orange-500/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-teal-500/20 rounded-full blur-3xl"></div>
                </div>

                <div className="relative z-10 text-center text-white max-w-6xl mx-auto px-4 pt-20">
                    <div className="mb-8">
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 mb-8 hover:bg-white/20 transition-colors duration-200 border border-white/20"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <span className="font-semibold">Back to Home</span>
                        </Link>
                    </div>

                    <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 mb-8 border border-white/20">
                        <Crown className="h-5 w-5 text-amber-300" />
                        <span className="text-sm font-semibold">Cinnamomum Verum</span>
                        <Sparkles className="h-4 w-4 text-amber-300" />
                    </div>

                    <h1 className="text-6xl md:text-8xl font-black mb-6 leading-tight">
                        <span className="bg-gradient-to-r from-white via-emerald-100 to-white bg-clip-text text-transparent">
                            Ceylon
                        </span>
                        <br />
                        <span className="bg-gradient-to-r from-amber-300 via-orange-300 to-amber-300 bg-clip-text text-transparent">
                            Cinnamon
                        </span>
                    </h1>

                    <p className="text-2xl md:text-3xl mb-6 font-light text-emerald-100">
                        The World&apos;s Only True Cinnamon
                    </p>

                    <p className="text-lg mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed">
                        Scientifically known as Cinnamomum verum, Ceylon Cinnamon is nature&apos;s most precious spice,
                        cultivated exclusively in Sri Lanka&apos;s mystical highlands for over 2,000 years.
                    </p>
                </div>
            </section>

            {/* What Makes It Special */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 rounded-full px-6 py-3 mb-6">
                            <Star className="h-5 w-5" />
                            <span className="font-semibold">What Makes It Special</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-6">
                            The Crown Jewel of
                            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Spices</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Unlike common cassia cinnamon, Ceylon Cinnamon offers unparalleled quality, safety, and health benefits
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group text-center">
                            <div className="relative mb-8">
                                <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                                    <Crown className="h-12 w-12 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                                    <Sparkles className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">True Cinnamon</h3>
                            <p className="text-gray-600 leading-relaxed">
                                The only authentic cinnamon species (Cinnamomum verum) recognized by botanists worldwide,
                                native exclusively to Sri Lanka.
                            </p>
                        </div>

                        <div className="group text-center">
                            <div className="relative mb-8">
                                <div className="w-24 h-24 bg-gradient-to-br from-rose-500 to-pink-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                                    <Heart className="h-12 w-12 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                                    <CheckCircle className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Ultra-Low Coumarin</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Contains less than 0.004% coumarin compared to cassia&apos;s 5%, making it completely safe
                                for daily consumption and therapeutic use.
                            </p>
                        </div>

                        <div className="group text-center">
                            <div className="relative mb-8">
                                <div className="w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                                    <Sparkles className="h-12 w-12 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center">
                                    <Star className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Exquisite Flavor</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Delicate, sweet, and complex flavor profile with subtle citrus notes that enhance
                                both sweet and savory culinary creations.
                            </p>
                        </div>

                        <div className="group text-center">
                            <div className="relative mb-8">
                                <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-3xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-500 shadow-2xl">
                                    <Award className="h-12 w-12 text-white" />
                                </div>
                                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                                    <Crown className="h-4 w-4 text-white" />
                                </div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Global Recognition</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Preferred by world-renowned chefs, health experts, and discerning consumers
                                who demand the finest quality spices.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Cultivation Process */}
            <section className="py-24 bg-gradient-to-br from-slate-50 to-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 rounded-full px-4 py-2 mb-6">
                                <Mountain className="h-4 w-4" />
                                <span className="text-sm font-semibold">From Garden to Global</span>
                            </div>

                            <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
                                Ancient Wisdom Meets
                                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Modern Excellence</span>
                            </h2>

                            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                                Our Ceylon Cinnamon journey begins in Sri Lanka&apos;s pristine central highlands,
                                where generations of skilled artisans have perfected the art of cinnamon cultivation.
                            </p>

                            <div className="space-y-8">
                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Mountain className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Highland Terroir</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Grown at optimal altitudes of 500-2000 meters where the unique combination of
                                            tropical climate, rich soil, and monsoon rains creates the perfect environment
                                            for premium cinnamon trees.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Droplets className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Artisan Processing</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Hand-peeled by master craftsmen using traditional techniques passed down through
                                            centuries, ensuring each quill maintains its delicate structure and aromatic oils.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Shield className="h-8 w-8 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Quality Assurance</h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            Every batch undergoes rigorous testing for purity, moisture content, and essential
                                            oil concentration to ensure only the finest cinnamon reaches international markets.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-8 -left-8 w-32 h-32 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-3xl transform rotate-12"></div>
                            <div className="relative bg-white rounded-3xl p-8 shadow-2xl border border-gray-100">
                                <img
                                    src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
                                    alt="Traditional cinnamon processing in Sri Lanka"
                                    className="w-full h-80 object-cover rounded-2xl mb-6"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 shadow-xl text-white">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold mb-1">2000+</div>
                                        <div className="text-xs opacity-90">Years Heritage</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Health Benefits */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 bg-rose-100 text-rose-700 rounded-full px-6 py-3 mb-6">
                            <Heart className="h-5 w-5" />
                            <span className="font-semibold">Health & Wellness</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-6">
                            Nature&apos;s Most Powerful
                            <span className="bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent"> Wellness Spice</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Ceylon Cinnamon isn&apos;t just a culinary delight—it&apos;s a scientifically proven wellness enhancer
                            with remarkable health benefits
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Cards... (unchanged content below) */}
                        {/* 1 */}
                        <div className="group bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-rose-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Heart className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Cardiovascular Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Supports healthy cholesterol levels and promotes optimal blood circulation,
                                    contributing to overall heart health and cardiovascular wellness.
                                </p>
                                <div className="flex items-center text-rose-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>Clinically Studied</span>
                                </div>
                            </div>
                        </div>

                        {/* 2 */}
                        <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-blue-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Thermometer className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Blood Sugar Balance</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    May help support healthy blood sugar levels and improve insulin sensitivity,
                                    making it beneficial for metabolic health management.
                                </p>
                                <div className="flex items-center text-blue-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>Research Backed</span>
                                </div>
                            </div>
                        </div>

                        {/* 3 */}
                        <div className="group bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-emerald-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Shield className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Antioxidant Powerhouse</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Packed with powerful antioxidants that help protect cells from oxidative stress
                                    and support overall health and longevity.
                                </p>
                                <div className="flex items-center text-emerald-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>High ORAC Value</span>
                                </div>
                            </div>
                        </div>

                        {/* 4 */}
                        <div className="group bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-amber-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Zap className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Anti-Inflammatory</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Natural anti-inflammatory compounds help reduce inflammation markers
                                    and support joint health and overall comfort.
                                </p>
                                <div className="flex items-center text-amber-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>Natural Compounds</span>
                                </div>
                            </div>
                        </div>

                        {/* 5 */}
                        <div className="group bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-purple-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <Star className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Cognitive Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    May support brain health and cognitive function through neuroprotective
                                    compounds and enhanced blood flow to the brain.
                                </p>
                                <div className="flex items-center text-purple-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>Neuroprotective</span>
                                </div>
                            </div>
                        </div>

                        {/* 6 */}
                        <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-green-100 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full blur-2xl transform translate-x-16 -translate-y-16"></div>
                            <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                    <CheckCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-4">Safe Daily Use</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Unlike cassia cinnamon, Ceylon Cinnamon&apos;s ultra-low coumarin content
                                    makes it completely safe for daily consumption and therapeutic doses.
                                </p>
                                <div className="flex items-center text-green-600 font-semibold text-sm">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    <span>FDA Recognized</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Ceylon vs Cassia Comparison */}
            <section className="py-24 bg-gradient-to-br from-slate-50 to-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 bg-amber-100 text-amber-700 rounded-full px-6 py-3 mb-6">
                            <Globe className="h-5 w-5" />
                            <span className="font-semibold">The Difference</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-6">
                            Ceylon vs. Cassia:
                            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent"> The Truth Revealed</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Understanding why Ceylon Cinnamon is the premium choice for health-conscious consumers and culinary professionals
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Ceylon Cinnamon */}
                        <div className="bg-white rounded-3xl p-10 shadow-2xl border-4 border-emerald-200 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
                            <div className="relative">
                                <div className="text-center mb-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                                        <Crown className="h-10 w-10 text-white" />
                                    </div>
                                    <h3 className="text-3xl font-black text-gray-900">Ceylon Cinnamon</h3>
                                    <p className="text-emerald-700 font-bold text-lg">(Cinnamomum verum)</p>
                                    <div className="inline-flex items-center space-x-1 bg-emerald-100 text-emerald-700 rounded-full px-3 py-1 mt-2">
                                        <Crown className="h-3 w-3" />
                                        <span className="text-xs font-bold">THE TRUE CINNAMON</span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Sweet, delicate, complex flavor</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Ultra-low coumarin</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Light tan to golden brown color</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Thin, paper-like bark layers</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Safe for daily consumption</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Premium health benefits</span>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium">Exclusively from Sri Lanka</span>
                                    </div>
                                </div>

                                <div className="mt-8 p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                                    <div className="text-center">
                                        <div className="text-2xl font-black text-emerald-700 mb-1">Premium Choice</div>
                                        <div className="text-sm text-emerald-600">For Health & Culinary Excellence</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cassia Cinnamon */}
                        <div className="bg-gray-100 rounded-3xl p-10 shadow-lg border-2 border-gray-300 relative">
                            <div className="text-center mb-8">
                                <div className="w-20 h-20 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Leaf className="h-10 w-10 text-white" />
                                </div>
                                <h3 className="text-3xl font-black text-gray-700">Cassia Cinnamon</h3>
                                <p className="text-gray-500 font-bold text-lg">(Common Cinnamon)</p>
                                <div className="inline-flex items-center space-x-1 bg-gray-200 text-gray-600 rounded-full px-3 py-1 mt-2">
                                    <span className="text-xs font-bold">COMMON SUBSTITUTE</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-gray-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">Strong, spicy, one-dimensional</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-red-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">High coumarin content (up to 5%)</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-gray-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">Dark reddish-brown color</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-gray-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">Thick, hard bark rolls</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-red-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">Limited daily consumption</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-gray-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">Basic properties only</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className="h-6 w-6 bg-gray-400 rounded-full flex-shrink-0"></div>
                                    <span className="text-gray-600">From China, Indonesia, Vietnam</span>
                                </div>
                            </div>

                            <div className="mt-8 p-4 bg-gray-200 rounded-2xl border border-gray-300">
                                <div className="text-center">
                                    <div className="text-2xl font-black text-gray-600 mb-1">Budget Option</div>
                                    <div className="text-sm text-gray-500">Limited Quality & Safety</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Product Showcase */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-20">
                        <div className="inline-flex items-center space-x-2 bg-amber-100 text-amber-700 rounded-full px-6 py-3 mb-6">
                            <Crown className="h-5 w-5" />
                            <span className="font-semibold">Our Products</span>
                        </div>
                        <h2 className="text-5xl font-black text-gray-900 mb-6">
                            Premium Ceylon Cinnamon
                            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent"> Collection</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Available in various forms to meet your specific culinary and wellness needs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-10">
                        {/* Product cards (unchanged content) */}
                        <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100">
                            <div className="relative">
                                <img
                                    src="https://images.pexels.com/photos/4198951/pexels-photo-4198951.jpeg"
                                    alt="Ceylon Cinnamon sticks"
                                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                    Premium
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">Cinnamon Quills</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Hand-rolled premium Ceylon Cinnamon sticks, perfect for brewing teas,
                                    flavoring dishes, and creating aromatic experiences.
                                </p>
                                <div className="space-y-2 mb-6">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                                        <span>Available: 5cm, 10cm, 15cm lengths</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                                        <span>Grade A quality certification</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-emerald-600 mr-2" />
                                        <span>Bulk and retail packaging</span>
                                    </div>
                                </div>
                                <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200">
                                    <div className="text-emerald-700 font-bold text-lg">Most Popular</div>
                                    <div className="text-emerald-600 text-sm">For tea and culinary use</div>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100">
                            <div className="relative">
                                <img
                                    src="https://images.pexels.com/photos/4198939/pexels-photo-4198939.jpeg"
                                    alt="Ground Ceylon Cinnamon powder"
                                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                    Versatile
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">Ground Powder</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Finely ground Ceylon Cinnamon powder, ideal for baking, cooking,
                                    smoothies, and supplement applications.
                                </p>
                                <div className="space-y-2 mb-6">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-amber-600 mr-2" />
                                        <span>Food grade &amp; supplement grade</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-amber-600 mr-2" />
                                        <span>Fine mesh consistency</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-amber-600 mr-2" />
                                        <span>Moisture-sealed packaging</span>
                                    </div>
                                </div>
                                <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
                                    <div className="text-amber-700 font-bold text-lg">Best for Baking</div>
                                    <div className="text-amber-600 text-sm">Easy to use and measure</div>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-gradient-to-br from-white to-gray-50 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100">
                            <div className="relative">
                                <img
                                    src="https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg"
                                    alt="Ceylon Cinnamon essential oil"
                                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                                    Therapeutic
                                </div>
                            </div>
                            <div className="p-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">Essential Oil</h3>
                                <p className="text-gray-600 mb-6 leading-relaxed">
                                    Pure Ceylon Cinnamon leaf oil, steam-distilled for aromatherapy,
                                    wellness applications, and natural health products.
                                </p>
                                <div className="space-y-2 mb-6">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-purple-600 mr-2" />
                                        <span>100% pure, no additives</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-purple-600 mr-2" />
                                        <span>Steam distilled extraction</span>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-600">
                                        <CheckCircle className="h-4 w-4 text-purple-600 mr-2" />
                                        <span>Dark glass bottles</span>
                                    </div>
                                </div>
                                <div className="bg-purple-50 rounded-2xl p-4 border border-purple-200">
                                    <div className="text-purple-700 font-bold text-lg">Wellness Focus</div>
                                    <div className="text-purple-600 text-sm">Aromatherapy &amp; health</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-r from-emerald-900 via-teal-800 to-emerald-900 relative overflow-hidden">
                <div className="absolute inset-0">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
                    <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md rounded-full px-6 py-3 mb-8 border border-white/20">
                        <Crown className="h-5 w-5 text-amber-300" />
                        <span className="text-white font-semibold">Ready to Import Premium Ceylon Cinnamon?</span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
                        Experience the
                        <span className="bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent"> True Cinnamon Difference</span>
                    </h2>

                    <p className="text-xl text-emerald-100 mb-12 leading-relaxed max-w-3xl mx-auto">
                        Join Australia&apos;s leading importers and health-conscious consumers who choose authentic Ceylon Cinnamon
                        for its unmatched quality, safety, and health benefits.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#contact"
                            className="group bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-3xl flex items-center space-x-2"
                        >
                            <span>Get Wholesale Pricing</span>
                            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                        <a
                            href="mailto:exports@claudesspecial.lk"
                            className="bg-white/10 hover:bg-white/20 text-white border-2 border-white/30 hover:border-white/50 px-10 py-5 rounded-full font-bold text-lg transition-all duration-300 backdrop-blur-md"
                        >
                            Email Direct
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-3 mb-6">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                                <Crown className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold">Claude&apos;s Special</div>
                                <div className="text-sm text-emerald-400">Authentic Ceylon Cinnamon</div>
                            </div>
                        </div>
                        <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                            The world&apos;s finest Ceylon Cinnamon, sourced directly from Sri Lanka&apos;s pristine highlands
                            and delivered to Australia with uncompromising quality and authenticity.
                        </p>
                        <div className="flex justify-center space-x-8 text-sm text-gray-400 mb-8">
                            <span className="flex items-center space-x-2">
                                <Crown className="h-4 w-4 text-amber-400" />
                                <span>Premium Quality</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Shield className="h-4 w-4 text-emerald-400" />
                                <span>Safe Daily Use</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Globe className="h-4 w-4 text-blue-400" />
                                <span>Global Export</span>
                            </span>
                        </div>
                        <p className="text-gray-500 text-sm">
                            &copy; 2025 Claude&apos;s Special. All rights reserved. | Authentic Ceylon Cinnamon Exports
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default CinnamonPage;
